import React, { useEffect, useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useJwtAuth } from './useJwtLogin';
import Loading from './Loading';

type RouteParams = {
  organizationId?: string;
};

type Props = {
  uuid: string;
};

const LoginWithUUID: React.FC<React.PropsWithChildren<Props>> = ({ uuid }) => {
  const { organizationId } = useParams<RouteParams>();
  const history = useHistory();
  const { t } = useTranslation();
  const { doLoginByUUID } = useJwtAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The login link is single-use: the token is consumed on the first attempt.
  // Guard against StrictMode's double-invoke so we never fire a second request
  // with an already-spent token (which would surface a spurious error).
  const hasAttemptedLogin = useRef(false);

  useEffect(() => {
    if (!organizationId || !uuid) {
      setIsLoading(false);
      setError(t('signIn.uuidError'));
      return;
    }

    if (hasAttemptedLogin.current) {
      return;
    }
    hasAttemptedLogin.current = true;

    doLoginByUUID(uuid, organizationId)
      .then((result) => {
        setIsLoading(false);
        if (result !== 'success') {
          setError(t('signIn.uuidError'));
        }
      })
      .catch(() => {
        setIsLoading(false);
        setError(t('signIn.uuidError'));
      });
  }, [doLoginByUUID, uuid, organizationId, t]);

  const handleBackToLogin = () => {
    history.push('/login');
  };

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div>
        <h4 className="alert-heading">{t('signIn.uuidErrorTitle')}</h4>
        <p>{error}</p>
        <hr />
        <button
          className="btn btn-primary"
          onClick={handleBackToLogin}
          type="button">
          {t('signIn.backToLogin')}
        </button>
      </div>
    );
  }

  return <></>;
};

export default LoginWithUUID;
